import { useEffect, useRef } from "react";
import { Platform } from "react-native";

type DeepLinkAutoSubmitStatus = "submitting" | "error_missing_data" | "error_not_web";

/**
 * Orchestrates the LTI deep-link form POST.
 *
 * Creates a hidden HTML form, appends a JWT hidden input, and submits it to
 * the LMS `deepLinkReturnUrl`. The LTI spec requires this to be a browser
 * form POST (not an API call) because the LMS expects it as a page navigation.
 *
 * Returns the current status so the caller can render the appropriate UI.
 */
function useDeepLinkAutoSubmit(jwt: string, deepLinkReturnUrl: string): DeepLinkAutoSubmitStatus {
  const submittedRef = useRef(false);

  const isValid = Boolean(jwt) && Boolean(deepLinkReturnUrl);
  const isWeb = Platform.OS === "web" && typeof document !== "undefined";

  useEffect(() => {
    if (!isValid || !isWeb || submittedRef.current) {
      return;
    }
    submittedRef.current = true;

    const form = document.createElement("form");
    form.method = "POST";
    form.action = deepLinkReturnUrl;
    form.style.display = "none";

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "JWT";
    input.value = jwt;
    form.appendChild(input);

    document.body.appendChild(form);
    form.submit();
  }, [jwt, deepLinkReturnUrl, isValid, isWeb]);

  if (!isWeb) {
    return "error_not_web";
  }
  if (!isValid) {
    return "error_missing_data";
  }
  return "submitting";
}

export type { DeepLinkAutoSubmitStatus };
export default useDeepLinkAutoSubmit;
